import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ModuleCard = ({ 
  module,
  onStartModule,
  onContinueModule,
  onViewModule 
}) => {
  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return 'CheckCircle';
      case 'in-progress': return 'Clock';
      case 'locked': return 'Lock';
      default: return 'Play';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-success';
      case 'in-progress': return 'text-warning';
      case 'locked': return 'text-muted-foreground';
      default: return 'text-primary';
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Beginner': return 'bg-green-100 text-green-800';
      case 'Intermediate': return 'bg-yellow-100 text-yellow-800';
      case 'Advanced': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleModuleAction = () => {
    switch (module.status) {
      case 'completed':
        onViewModule(module);
        break;
      case 'in-progress':
        onContinueModule(module);
        break;
      case 'locked':
        break;
      default:
        onStartModule(module);
    }
  };

  const getActionText = () => {
    switch (module.status) {
      case 'completed': return 'Review';
      case 'in-progress': return 'Continue';
      case 'locked': return 'Locked';
      default: return 'Start';
    }
  };

  return (
    <div className={`bg-card border border-border rounded-lg p-6 transition-all duration-200 hover:shadow-elevated ${module.status === 'locked' ? 'opacity-60' : 'hover:border-primary/20'}`}>
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className={`w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center`}>
            <Icon name={module.icon} size={20} className="text-primary" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground">{module.title}</h3>
            <div className="flex items-center space-x-2 mt-1">
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(module.difficulty)}`}>
                {module.difficulty}
              </span>
              <span className="text-sm text-muted-foreground">{module.duration}</span>
            </div>
          </div>
        </div>
        <Icon 
          name={getStatusIcon(module.status)} 
          size={20} 
          className={getStatusColor(module.status)} 
        />
      </div>
      <p className="text-muted-foreground text-sm mb-4 line-clamp-2">
        {module.description}
      </p>
      {/* Learning Objectives */}
      <div className="mb-4">
        <h4 className="text-sm font-medium text-foreground mb-2">Learning Objectives:</h4>
        <ul className="space-y-1">
          {module.objectives?.slice(0, 2)?.map((objective, index) => (
            <li key={index} className="flex items-start space-x-2 text-sm text-muted-foreground">
              <Icon name="Check" size={14} className="text-success mt-0.5 flex-shrink-0" />
              <span>{objective}</span>
            </li>
          ))}
          {module.objectives?.length > 2 && (
            <li className="text-sm text-muted-foreground ml-5">
              +{module.objectives?.length - 2} more objectives
            </li>
          )}
        </ul>
      </div>
      {/* Prerequisites */}
      {module.prerequisites && module.prerequisites?.length > 0 && (
        <div className="mb-4">
          <h4 className="text-sm font-medium text-foreground mb-2">Prerequisites:</h4>
          <div className="flex flex-wrap gap-1">
            {module.prerequisites?.map((prereq, index) => (
              <span key={index} className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded">
                {prereq}
              </span>
            ))}
          </div>
        </div>
      )}
      {/* Progress Bar for in-progress modules */}
      {module.status === 'in-progress' && (
        <div className="mb-4">
          <div className="flex justify-between text-sm text-muted-foreground mb-1">
            <span>Progress</span>
            <span>{module.progress}%</span>
          </div>
          <div className="w-full bg-muted rounded-full h-2">
            <div 
              className="bg-primary h-2 rounded-full transition-all duration-300"
              style={{ width: `${module.progress}%` }}
            ></div>
          </div>
        </div>
      )}
      {/* Skills & Badges */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          {module.skills?.slice(0, 3)?.map((skill, index) => (
            <span key={index} className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-full">
              {skill}
            </span>
          ))}
          {module.skills?.length > 3 && (
            <span className="text-xs text-muted-foreground">+{module.skills?.length - 3}</span>
          )}
        </div>
        
        <Button
          variant={module.status === 'completed' ? 'outline' : 'default'}
          size="sm"
          onClick={handleModuleAction}
          disabled={module.status === 'locked'}
          iconName={module.status === 'completed' ? 'Eye' : 'Play'}
          iconPosition="left"
        >
          {getActionText()}
        </Button>
      </div>
    </div>
  );
};

export default ModuleCard;